export const INCOMING_INVOICES_SORTER_FORM_DEFAULT_PARAMS = Object.freeze({});

export const INCOMING_INVOICES_FILTER_FORM_DEFAULT_PARAMS = Object.freeze({
  // The issue date, offered by the filter panel. One of the book's columns, and
  // independent of the month picker: the month is its own filter now, so
  // narrowing by issue date no longer silently redefines which month is shown.
  documentDate: undefined,
  // Listed so "clear all filters" actually clears it — anything missing from
  // this object survives the reset and goes on filtering a table that claims to
  // be unfiltered.
  approvedAt: undefined,
  // When the project was last corrected — offered in the panel and the header.
  projectChange: undefined,
  journalPeriod: undefined,
  dueDate: undefined,
  deliveryDate: undefined,
  products: undefined,
  createdAt: undefined,
  updatedAt: undefined,
  // The printed mark, offered in this panel.
  printed: undefined,
  // Stornoed by the supplier after we had approved it, offered in this panel.
  stornoAfterApproval: undefined,
  // Paid / partly paid from the bank statements, offered in this panel.
  payment: undefined,
  // Article 10 / without VAT / with VAT, offered in this panel.
  vatType: undefined,
  // Sent to the bank in a payment order, offered in this panel.
  paymentOrder: undefined,
});

export const INCOMING_INVOICES_COLUMNS_DEFAULT_PARAMS = Object.freeze({
  // filters
  documentNumber: undefined,
  salesInvoiceId: undefined,
  total: undefined,
  customerName: undefined,
  status: undefined,
  supplierName: undefined,
  purchaseInvoiceId: undefined,
  internalReferenceNumber: undefined,
  project: undefined,
  origin: undefined,
  // The book's columns. Listed here so "clear all filters" actually clears
  // them — anything missing from this object survives the reset and keeps
  // filtering a table that claims to be unfiltered.
  documentType: undefined,
  supplierVat: undefined,
  vatDeductible: undefined,
  approvedBy: undefined,
  approvalReminder: undefined,
  postedToErp: undefined,
  // The payment column's funnel.
  payment: undefined,
});

export const INCOMING_INVOICES_DEFAULT_PARAMS = Object.freeze({
  // defaults
  offset: '1',
  // 15 and not the 10 every other list uses: people approve invoices from
  // this screen, and a small company's whole month fits on one page.
  limit: '15',

  // filters

  // sorters
  sortDocumentNumber: undefined,
  sortPurchaseInvoiceId: undefined,
  sortDeliveryDate: undefined,
  sortDueDate: undefined,
  sortSupplierName: undefined,
  sortInternalReferenceNumber: undefined,
  sortStatus: undefined,
  sortUpdatedAt: undefined,
  sortDocumentDate: undefined,
  sortTotalWithVat: undefined,
  sortVatAmount: undefined,

  // Which of the two views the screen is in. Kept in the query params, not in
  // component state, so the view survives a reload and a shared link — the book
  // is something people send each other a link to.
  view: undefined,
  // Only invoices with a message waiting for the person looking.
  unreadMessages: undefined,
  ...INCOMING_INVOICES_SORTER_FORM_DEFAULT_PARAMS,
  ...INCOMING_INVOICES_FILTER_FORM_DEFAULT_PARAMS,
  ...INCOMING_INVOICES_COLUMNS_DEFAULT_PARAMS,
});

export const INCOMING_INVOICES_FIELD_MAP = {
  documentNumber: {
    path: 'referenceId.documentNumber',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.documentNumber',
    filterKey: 'documentNumber',
  },
  purchaseInvoiceId: {
    path: 'referenceId.purchaseInvoiceId',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.purchaseInvoiceId',
    filterKey: 'purchaseInvoiceId',
  },
  total: {
    path: 'totalAmount',
    isRef: false,
    type: 'number',
    sortKey: 'totalAmount',
    filterKey: 'totalAmount',
  },
  supplierName: {
    path: 'referenceId.supplierName',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.supplierName',
    filterKey: 'supplierName',
  },
  status: {
    path: 'status',
    isRef: false,
    type: 'array',
    sortKey: 'status',
  },
  deliveryDate: {
    path: 'referenceId.deliveryDate',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.deliveryDate',
  },
  dueDate: {
    path: 'referenceId.dueDate',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.dueDate',
  },
  updatedAt: {
    path: 'updatedAt',
    isRef: false,
    type: 'date',
    sortKey: 'updatedAt',
  },
  internalReferenceNumber: {
    path: 'referenceId.internalReferenceNumber',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.internalReferenceNumber',
    filterKey: 'internalReferenceNumber',
  },
  // `array` and not `text`: the value is an id, and a regex over an ObjectId
  // matches nothing. The server casts it and understands `PROJECT_NONE`.
  project: {
    path: 'referenceId.project',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.project',
    filterKey: 'project',
  },
  transactionDescription: {
    path: 'referenceId.transactionDescription',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.transactionDescription',
    filterKey: 'transactionDescription',
  },
  // Whether we have printed it yet. On the root invoice, so not a reference
  // field; `array` because the question is "printed or not" — the server turns
  // `printed` / `__none__` into a presence test.
  printed: {
    path: 'printed.at',
    isRef: false,
    type: 'array',
    sortKey: 'printed.at',
  },
  // Stornoed by the supplier at SEF after we had approved it — the invoices
  // whose VAT return may need correcting. A presence question like the posting
  // mark below, so `array` and a wire value the server turns into `$ne: null`.
  // Paid / partly paid, from the bank statements. Exact keys, and `__none__`
  // for "no payment found" — which the server reads as empty or absent.
  payment: {
    path: 'referenceId.payment.status',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.payment.status',
    filterKey: 'payment.status',
  },
  // Put into a payment order here: `sent`, `sentUnpaid` or `__none__`. Not a
  // stored value — the server turns each into a condition on
  // `paymentOrder.sentAt` (and the payment, for `sentUnpaid`).
  paymentOrder: {
    path: 'referenceId.paymentOrder.sentAt',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.paymentOrder.sentAt',
    filterKey: 'paymentOrder',
  },
  // The kind of VAT on the document (support ticket #37). Not a stored field:
  // the server turns each value into a condition on the document's tax
  // categories — see `coerceRefFiltersHelper`. Filter only, nothing to sort.
  vatType: {
    path: 'referenceId.taxCategories',
    isRef: true,
    type: 'array',
    filterKey: 'vatType',
  },
  stornoAfterApproval: {
    path: 'referenceId.stornoAfterApprovalAt',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.stornoAfterApprovalAt',
    filterKey: 'stornoAfterApprovalAt',
  },
  // Our own bookkeeping mark. `array` and not `date`: the question people ask
  // is "booked or not", so the filter sends `yes`/`no` and the server turns
  // that into a presence test — a date range over it would answer a question
  // nobody has. One entry here buys both the filter and the sorter.
  postedToErp: {
    path: 'referenceId.postedToErp.at',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.postedToErp.at',
    filterKey: 'postedToErp.at',
  },
  // `array` and not `text`: the value is an enum key, so a "contains" regex
  // would match `sef` inside nothing and cost a collection scan besides. The
  // server turns a request for `sef` into "not manual", because the invoices
  // stored before the field existed have no `origin` at all.
  origin: {
    path: 'referenceId.origin',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.origin',
    filterKey: 'origin',
  },
  manualDocumentType: {
    path: 'referenceId.manualDocumentType',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.manualDocumentType',
    filterKey: 'manualDocumentType',
  },
  // ── The book's own columns ────────────────────────────────────────────────
  //
  // The date the supplier issued the document, which is what their book sorts
  // and closes a month by. 🚨 Not `deliveryDate`: that is the date of supply,
  // and the inbox has been showing it under a "due date" heading for years.
  documentDate: {
    path: 'referenceId.documentDate',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.documentDate',
    filterKey: 'documentDate',
  },
  // When we accepted the invoice. This is the date that decides which tax period
  // it is claimed in — accepted by the 10th still belongs to the month before —
  // so the book needs to sort and filter by it, not only display it.
  // 🚨 A real Date on the model, unlike every other date field here, which are
  // strings. The server has a separate branch for it; a string comparison
  // against a BSON date matches nothing and says nothing.
  approvedAt: {
    path: 'referenceId.approvedAt',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.approvedAt',
    filterKey: 'approvedAt',
  },
  // When somebody last moved the invoice to another project. A real Date, like
  // `approvedAt`, and handled by the same server branch. Not the acceptance
  // date: that one decides the tax period and does not move with the project.
  projectChange: {
    path: 'referenceId.projectChange.at',
    isRef: true,
    type: 'date',
    sortKey: 'referenceId.projectChange.at',
    filterKey: 'projectChange.at',
  },
  // Who accepted it, searched by name.
  //
  // 🚨 `default` and NOT `text`, although a name is text: a `text` field is
  // wrapped in `{ $regex }` here, and the stored value is a user id, so the
  // pattern would be compared against an ObjectId and match nothing — an empty
  // table that reads as "nobody approved anything". The typed name goes out
  // whole and the server resolves it to the people it names
  // (`resolveApproverNameFilterHelper`), which is also what lets one word reach
  // either half of a name and two words match in any order.
  //
  // 🚨 No `sortKey`: sorting by this column would order by the id underneath,
  // which is the order the user accounts were created in — arrows that move the
  // table into an order nobody asked for.
  approvedBy: {
    path: 'referenceId.approvedBy',
    isRef: true,
    type: 'default',
    filterKey: 'approvedBy',
  },
  // SEF's own sentence about a refusal, kept for display only.
  //
  // No `sortKey` or `filterKey` on purpose: measured across two companies it
  // reads `Automatski odbijeno` on every refusal we hold, so a filter would
  // offer one value and a sorter would order by a constant. It earns its place
  // beside the status, where a refused row otherwise explains nothing.
  rejectOrStornoComment: {
    path: 'referenceId.rejectOrStornoComment',
    isRef: true,
    type: 'string',
  },
  // The month the invoice is filed under in the book, as `YYYY-MM`. Not a date
  // and not a range: the whole point of storing it is that one equality answers
  // "show me July", where the three dates it is derived from cannot.
  journalPeriod: {
    path: 'referenceId.journalPeriod',
    isRef: true,
    type: 'default',
    sortKey: 'referenceId.journalPeriod',
    filterKey: 'journalPeriod',
  },
  documentType: {
    path: 'referenceId.documentType',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.documentType',
    filterKey: 'documentType',
  },
  supplierVat: {
    path: 'referenceId.supplierVat',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.supplierVat',
    filterKey: 'supplierVat',
  },
  // 🚨 The gross amount, and NOT `total`. `total` is the payable amount net of
  // any prepayment: measured, it runs below the net amount on 1.448 of 12.890
  // stored invoices, one of them showing 3,49 for a document worth 286.377,60.
  // 🚨 `sortOnly`, and that flag is the whole declaration: the allow-list
  // `prepareFilters` gets is read off this map, so a column left unmarked
  // becomes filterable and a column marked here cannot be. The book offers no
  // numeric filter on these two, so they must not claim one — declaring a
  // column filterable while nothing accepts the filter is what made the
  // project filter silently do nothing for a whole phase.
  totalWithVat: {
    path: 'referenceId.totalWithVat',
    isRef: true,
    type: 'number',
    sortKey: 'referenceId.totalWithVat',
    sortOnly: true,
  },
  vatAmount: {
    path: 'referenceId.vatAmount',
    isRef: true,
    type: 'number',
    sortKey: 'referenceId.vatAmount',
    sortOnly: true,
  },
  vatDeductible: {
    path: 'referenceId.vatDeductible',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.vatDeductible',
    filterKey: 'vatDeductible',
  },
  // Whether the company's people have already been nudged about this document.
  //
  // 🔑 One entry buys both controls: the allow-list `prepareFilters` gets and
  // the sorter map are both derived from this map, which is the whole reason
  // this map exists — a column named in only one of the two used to sort
  // without filtering, or light its funnel and return the entire table.
  //
  // 🚨 `array` and not `date`. A date range over "when was it chased" answers a
  // question nobody asks; the working question is which rows nobody has chased
  // yet, so the funnel offers two values and the server reads them as presence
  // (`sent`) and absence (`__none__`) — see `presenceFields` in
  // `coerceRefFiltersHelper`. Sorting still runs on the date underneath.
  approvalReminder: {
    path: 'referenceId.approvalReminder.lastSentAt',
    isRef: true,
    type: 'array',
    sortKey: 'referenceId.approvalReminder.lastSentAt',
    filterKey: 'approvalReminder.lastSentAt',
  },
  // ── The company's own nine columns ────────────────────────────────────────
  //
  // Free text, all nine, including the two whose headings ask a yes-or-no
  // question — see `journalNotes` on the purchase model for the measurement
  // that settled it. Declared here and nowhere else: the filter allow-list and
  // the sorter map are read off this object, so these nine can be searched and
  // sorted without being named again anywhere.
  journalVatRefundApplied: {
    path: 'referenceId.journalNotes.vatRefundApplied',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.vatRefundApplied',
    filterKey: 'journalNotes.vatRefundApplied',
  },
  journalBooked: {
    path: 'referenceId.journalNotes.booked',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.booked',
    filterKey: 'journalNotes.booked',
  },
  journalPaid: {
    path: 'referenceId.journalNotes.paid',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.paid',
    filterKey: 'journalNotes.paid',
  },
  journalPaymentMethod: {
    path: 'referenceId.journalNotes.paymentMethod',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.paymentMethod',
    filterKey: 'journalNotes.paymentMethod',
  },
  journalAccountingNumberChina: {
    path: 'referenceId.journalNotes.accountingNumberChina',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.accountingNumberChina',
    filterKey: 'journalNotes.accountingNumberChina',
  },
  journalVatRefundedAmount: {
    path: 'referenceId.journalNotes.vatRefundedAmount',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.vatRefundedAmount',
    filterKey: 'journalNotes.vatRefundedAmount',
  },
  journalProjectTeam: {
    path: 'referenceId.journalNotes.projectTeam',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.projectTeam',
    filterKey: 'journalNotes.projectTeam',
  },
  journalAccountingNumberSerbia: {
    path: 'referenceId.journalNotes.accountingNumberSerbia',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.accountingNumberSerbia',
    filterKey: 'journalNotes.accountingNumberSerbia',
  },
  journalRemark: {
    path: 'referenceId.journalNotes.remark',
    isRef: true,
    type: 'text',
    sortKey: 'referenceId.journalNotes.remark',
    filterKey: 'journalNotes.remark',
  },
};

/**
 * The nine keys as the server names them, in the order of their own sheet.
 *
 * 🚨 The field-map keys above are prefixed (`journalBooked`) because a query
 * param called `booked` would collide with nothing today and with something
 * tomorrow; these are the unprefixed names the API takes.
 */
export const JOURNAL_NOTE_KEYS = Object.freeze([
  'vatRefundApplied',
  'booked',
  'paid',
  'paymentMethod',
  'accountingNumberChina',
  'vatRefundedAmount',
  'projectTeam',
  'accountingNumberSerbia',
  'remark',
]);

/**
 * Filter value standing for "no project assigned yet".
 *
 * Worth its own value rather than an empty filter: the invoices still missing a
 * project are a worklist someone has to clear, and an absent filter cannot ask
 * for them.
 */
export const PROJECT_NONE = '__none__';

/**
 * Origin keys, mirroring `server-hlb/enums/invoices.origin.js`.
 *
 * 🚨 `sef` is a request, not a stored value. Every invoice written before the
 * field existed has no `origin`, so the server reads a request for `sef` as
 * "not manual". Nothing on this side may test a row for equality with `sef`
 * either — use `isManualInvoice` below.
 */
export const INVOICE_ORIGIN = Object.freeze({
  sef: 'sef',
  manual: 'manual',
});

/**
 * Whether a row was entered by hand.
 *
 * A helper rather than a comparison spelled out at each site, because the safe
 * polarity is the counter-intuitive one: an absent `origin` means SEF.
 *
 * @param {Object} record - A row from the invoice list, or the referenced doc.
 * @returns {boolean}
 */
export const isManualInvoice = (record) =>
  (record?.referenceId?.origin ?? record?.origin) === INVOICE_ORIGIN.manual;

/**
 * The two ways of looking at the same list.
 *
 * `inbox` is what the screen has always been: what arrived from SEF and what
 * state it is in. `journal` is the book the company hands to their accounting
 * agency — a closed month of one project, with the columns that document needs.
 *
 * Deliberately one screen and two column sets rather than two screens. A second
 * list means a second place for the company guard to be forgotten, which is
 * precisely how invoices from one client ended up visible to another.
 */
export const INCOMING_VIEW = Object.freeze({
  inbox: 'inbox',
  journal: 'journal',
});

/**
 * VAT deductibility keys, mirroring `server-hlb/enums/invoices.vatDeductibility.js`.
 *
 * An empty value is a fourth state and not a missing one: it means the VAT on
 * that document was never established, so no verdict was reached. The column
 * shows it blank rather than as a decision nobody made.
 */
export const VAT_DEDUCTIBLE = Object.freeze({
  deductible: 'deductible',
  notDeductible: 'notDeductible',
  reverseCharge: 'reverseCharge',
});

/**
 * Filter value standing for "no verdict yet".
 *
 * Same reasoning as `PROJECT_NONE`: those rows are a worklist, and an absent
 * filter cannot ask for them.
 */
export const VAT_DEDUCTIBLE_NONE = '__none__';

// The two values the reminder funnel offers. `'__none__'` is the same sentinel
// the project and VAT filters use for "nothing filled in", and it is the half
// people actually reach for: it is the worklist of suppliers nobody has chased.
export const REMINDER_SENT = 'sent';
// The posting mark asks the same presence question as the reminder above, but
// with its own wire value so the query string reads as what the screen asked.
// The server accepts both spellings — see `PRESENT_VALUES` in queryDbHelpers.
export const POSTED_TO_ERP_YES = 'posted';
export const POSTED_TO_ERP_NO = '__none__';
export const REMINDER_NONE = '__none__';
// The printed mark asks the same presence question, with its own wire value.
export const PRINTED_YES = 'printed';
export const PRINTED_NO = '__none__';
// Stornoed after approval. Only the "yes" half is offered — "all the others"
// is what the list shows without the filter.
export const STORNO_AFTER_APPROVAL_YES = 'stornoAfterApproval';
// The VAT-kind filter's three answers; the server knows the same three words.
export const VAT_TYPE_REVERSE_CHARGE = 'reverseCharge';
export const VAT_TYPE_NO_VAT = 'noVat';
export const VAT_TYPE_WITH_VAT = 'withVat';
// The payment filter's three answers. Shared with the outgoing list.
export const PAYMENT_STATUS_PAID = 'paid';
export const PAYMENT_STATUS_PARTIAL = 'partial';
export const PAYMENT_STATUS_NONE = '__none__';
// The "sent to the bank" filter's three answers; the server knows the same words.
export const PAYMENT_ORDER_SENT = 'sent';
export const PAYMENT_ORDER_SENT_UNPAID = 'sentUnpaid';
export const PAYMENT_ORDER_NONE = '__none__';
